import api from './api';
import ErrorHandler from './errorHandler';

/**
 * Template Set API Service
 */
class TemplateSetService {
    /**
     * Get all template sets with filters
     * @param {Object} params - Query parameters
     * @param {number} params.page - Page number
     * @param {number} params.limit - Items per page
     * @param {string} params.search - Search query
     * @param {string} params.category - Category filter
     * @param {string} params.language - Language filter
     * @returns {Promise<Object>} Paginated template sets
     */
    async getAllTemplateSets(params = {}) {
        try {
            // Note: api.js interceptor already returns response.data
            const response = await api.get('/api/template-sets', { params });
            return response; // This is already the backend's response object
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    /**
     * Fuzzy "ready template" suggestions (Bangla/Banglish tolerant — the backend
     * matches by consonant skeleton). Returns [{ id, name, slug }] in data.
     */
    async suggestTemplateSets(params = {}) {
        try {
            const response = await api.get('/api/template-sets/suggest', { params });
            return response;
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    /**
     * Get template set by ID
     * @param {number} id - Template Set ID
     * @returns {Promise<Object>} Template Set details
     */
    async getTemplateSetById(id) {
        try {
            const response = await api.get(`/api/template-sets/${id}`);
            return response; // This is already the backend's response object
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    /**
     * Get template set statistics (admin only)
     * @returns {Promise<Object>} Template Set statistics
     */
    async getTemplateSetStats() {
        try {
            const response = await api.get('/api/template-sets/stats');
            return response; // This is already the backend's response object
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    /**
     * Create new template set (admin only)
     * @param {Object} data - Template Set data
     * @param {string} data.name - Template Set name
     * @param {string} data.description - Template Set description
     * @param {string} data.category - Template Set category
     * @param {string} data.language - Template Set language
     * @param {Array} data.placeholders - Placeholder definitions
     * @param {Array} data.docs - Document templates
     * @param {Array} data.tagIds - Associated tag IDs
     * @returns {Promise<Object>} Created template set
     */
    async createTemplateSet(data) {
        try {
            const response = await api.post('/api/template-sets', data);
            return response; // This is already the backend's response object
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    /**
     * Update template set (admin only)
     * @param {number} id - Template Set ID
     * @param {Object} data - Updated template set data
     * @returns {Promise<Object>} Updated template set
     */
    async updateTemplateSet(id, data) {
        try {
            const response = await api.put(`/api/template-sets/${id}`, data);
            return response; // This is already the backend's response object
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    /**
     * Delete template set (admin only)
     * @param {number} id - Template Set ID
     * @returns {Promise<Object>} Deletion confirmation
     */
    async deleteTemplateSet(id) {
        try {
            const response = await api.delete(`/api/template-sets/${id}`);
            return response; // This is already the backend's response object
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    /**
     * Update template document content (admin only)
     * @param {number} docId - Template document ID
     * @param {string} content - Updated content
     * @returns {Promise<Object>} Updated template document
     */
    async getTemplateDocById(docId) {
        try {
            const response = await api.get(`/api/template-sets/editor/${docId}`);
            return response;
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    async updatePlaceholderMetadata(templateSetId, slug, data) {
        try {
            const response = await api.patch(`/api/template-sets/${templateSetId}/placeholders/${encodeURIComponent(slug)}`, data);
            return response;
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    async createPlaceholder(templateSetId, data) {
        try {
            const response = await api.post(`/api/template-sets/${templateSetId}/placeholders`, data);
            return response;
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    async reorderPlaceholders(templateSetId, items) {
        try {
            const response = await api.put(`/api/template-sets/${templateSetId}/placeholders/reorder`, items);
            return response;
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    async deletePlaceholder(templateSetId, slug) {
        try {
            const response = await api.delete(`/api/template-sets/${templateSetId}/placeholders/${encodeURIComponent(slug)}`);
            return response;
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    async updateTemplateDocContent(docId, payload) {
        try {
            // payload can be { lexicalJson } (new) or a legacy string/object
            const body = typeof payload === 'object' && payload !== null ? payload : { content: payload };
            const response = await api.patch(`/api/template-sets/editor/${docId}`, body);
            return response;
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    async importDocContent(docId) {
        try {
            const response = await api.get(`/api/template-sets/editor/${docId}/import-doc`);
            return response;
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    async deleteTemplateDoc(docId) {
        try {
            const response = await api.delete(`/api/template-sets/editor/${docId}`);
            return response;
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    async duplicateTemplateDoc(docId) {
        try {
            const response = await api.post(`/api/template-sets/editor/${docId}/duplicate`);
            return response;
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    /**
     * Duplicate template set (admin only)
     * @param {number} id - Template Set ID to duplicate
     * @returns {Promise<Object>} Duplicated template set
     */
    async duplicateTemplateSet(id) {
        try {
            const response = await api.post(`/api/template-sets/${id}/duplicate`);
            return response; // This is already the backend's response object
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    /**
     * Save dummy preview values for every doc in a template set (admin only).
     * @param {number} id - Template Set ID
     * @param {Object} docs - { [docId]: { [slug]: string } } map
     */
    async updatePreviewValues(id, docs) {
        try {
            const response = await api.put(`/api/template-sets/${id}/preview-values`, { docs });
            return response;
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    async generateAiPreviewValues(id, placeholders) {
        try {
            const response = await api.post(`/api/template-sets/${id}/ai-preview-values`, { placeholders });
            return response;
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

}

export default new TemplateSetService();
